import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { apiService, getApiErrorMessage, PromptRun, Video } from '../services/api';
import { applyRealtimeStateToVideo, processingRealtimeStore } from '../services/processingRealtimeStore';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { getDisplayedVideoStatus, selectVideoRun } from '../utils/runScopedVideoStatus';
import { ensureDurableVideoPreviewFields, applyVideoAssetsReadyEvent } from '../utils/videoPreviewProjection';
import { sortVideos, VideoSortField } from '../utils/videoSorting';
import { DeferredPreviewTile, PreviewViewportProvider } from './GrantedPreviewImage';
import VideoTile from './VideoTile';
import WorkflowSortControls, { WorkflowSortField } from './WorkflowSortControls';
import './WorkflowVideoSelection.css';

interface WorkflowVideoSelectionProps {
  indexId: string;
  indexName: string;
  totalVideoCount?: number;
  runCatalog?: readonly PromptRun[];
  selectedVideoIds: string[] | null;
  onChange: (videoIds: string[] | null) => void;
  isActive: boolean;
  disabled?: boolean;
  selectionKey?: string;
  onValidityChange?: (valid: boolean) => void;
}

interface VideoDataset {
  indexId: string;
  videos: Video[];
  loading: boolean;
  complete: boolean;
  error: string | null;
}

const ignoreDelete = () => {};
const PAGE_SIZE = 50;
const EMPTY_VIDEOS: Video[] = [];
const EMPTY_RUNS: readonly PromptRun[] = [];
const SORT_FIELDS: readonly WorkflowSortField<VideoSortField>[] = [
  { field: 'name', label: 'Title', defaultAscending: true },
  { field: 'date', label: 'Date', defaultAscending: true },
  { field: 'status', label: 'Status', defaultAscending: true },
];

const WorkflowVideoSelection: React.FC<WorkflowVideoSelectionProps> = ({
  indexId,
  indexName,
  totalVideoCount,
  runCatalog = EMPTY_RUNS,
  selectedVideoIds,
  onChange,
  isActive,
  disabled = false,
  selectionKey,
  onValidityChange,
}) => {
  const groupId = useId();
  const canObserve = usePassiveApiActivity(false, isActive);
  const isSubset = selectedVideoIds !== null;
  const [expanded, setExpanded] = useState(isSubset);
  const [sort, setSort] = useState<{ field: VideoSortField; ascending: boolean }>({ field: 'date', ascending: false });
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [dataset, setDataset] = useState<VideoDataset | null>(null);
  const selectionRef = useRef(selectedVideoIds);
  selectionRef.current = selectedVideoIds;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const currentDataset = dataset?.indexId === indexId ? dataset : null;
  const videos = currentDataset?.videos || EMPTY_VIDEOS;
  const isLoading = !currentDataset || currentDataset.loading;
  const complete = Boolean(currentDataset?.complete);
  const allVideoCount = complete ? videos.length : totalVideoCount;
  const selectedIds = useMemo(() => new Set(selectedVideoIds || []), [selectedVideoIds]);
  const getEffectiveVideoStatus = useCallback((video: Video) => {
    const run = selectVideoRun(video.video_id, runCatalog);
    return getDisplayedVideoStatus(video, run?.run_id, run?.status);
  }, [runCatalog]);
  const sortedVideos = useMemo(() => sortVideos(videos, sort.field, sort.ascending, getEffectiveVideoStatus), [videos, sort, getEffectiveVideoStatus]);
  const valid = !isSubset || (complete && !isLoading && selectedIds.size > 0
    && videos.filter((video) => selectedIds.has(video.video_id)).length === selectedIds.size);

  useEffect(() => {
    onValidityChange?.(valid);
  }, [onValidityChange, valid]);

  useEffect(() => {
    if (isSubset) setExpanded(true);
  }, [indexId, isSubset, selectionKey]);

  useEffect(() => {
    if (!isActive || !isSubset || !indexId) return;
    let cancelled = false;
    setDataset((previous) => ({
      indexId,
      videos: previous?.indexId === indexId ? previous.videos : [],
      loading: true,
      complete: false,
      error: null,
    }));
    const load = async () => {
      const byId = new Map<string, Video>();
      let cursor: string | undefined;
      try {
        do {
          const page = await apiService.getVideosInIndex(indexId, PAGE_SIZE, cursor, { view: 'gallery' });
          if (cancelled) return;
          page.data.forEach((video) => {
            byId.set(video.video_id, applyRealtimeStateToVideo(
              ensureDurableVideoPreviewFields(video),
              processingRealtimeStore.getVideoState(video.video_id),
            ));
          });
          cursor = page.pagination.has_more ? page.pagination.next_cursor : undefined;
          if (page.pagination.has_more && !cursor) throw new Error('Videos could not be loaded.');
          const pageIds = new Set(page.data.map((video) => video.video_id));
          const hasMore = Boolean(cursor);
          setDataset((previous) => {
            const retained = new Map(previous?.indexId === indexId
              ? previous.videos.map((video) => [video.video_id, video])
              : []);
            return {
              indexId,
              videos: Array.from(byId, ([id, video]) => pageIds.has(id) ? video : retained.get(id) || video),
              loading: hasMore,
              complete: !hasMore,
              error: null,
            };
          });
        } while (cursor);
        // Only a complete membership snapshot can remove unavailable selections.
        // An empty subset remains explicit and can never become the whole index.
        const selected = selectionRef.current;
        if (selected) {
          const available = selected.filter((videoId) => byId.has(videoId));
          if (available.length !== selected.length) onChangeRef.current(available);
        }
      } catch (error) {
        if (cancelled) return;
        setDataset((previous) => ({
          indexId,
          videos: previous?.indexId === indexId ? previous.videos : [],
          loading: false,
          complete: false,
          error: getApiErrorMessage(error, 'Videos could not be loaded.'),
        }));
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [indexId, isActive, isSubset, refreshRevision, selectionKey]);

  const progressVideoIdsKey = JSON.stringify(sortedVideos.slice(0, 100).map((video) => video.video_id));
  useEffect(() => {
    if (!canObserve || !isSubset || !expanded) return;
    return processingRealtimeStore.subscribe((event) => {
      if (!event.video_id) return;
      setDataset((previous) => {
        if (previous?.indexId !== indexId) return previous;
        const position = previous.videos.findIndex((video) => video.video_id === event.video_id);
        if (position < 0) return previous;
        const video = previous.videos[position];
        const updated = applyRealtimeStateToVideo(
          applyVideoAssetsReadyEvent(video, event),
          processingRealtimeStore.getVideoState(video.video_id),
        );
        if (updated === video) return previous;
        const nextVideos = [...previous.videos];
        nextVideos[position] = updated;
        return { ...previous, videos: nextVideos };
      });
    }, { index_id: indexId, video_ids: JSON.parse(progressVideoIdsKey) });
  }, [indexId, canObserve, isSubset, expanded, progressVideoIdsKey]);

  const toggleVideo = (video: Video) => {
    const next = new Set(selectedIds);
    if (next.has(video.video_id)) next.delete(video.video_id);
    else next.add(video.video_id);
    onChange(Array.from(next));
  };
  const selectByStatus = (processed: boolean) => {
    onChange(videos.filter((video) => (getEffectiveVideoStatus(video) === 'processed') === processed).map((video) => video.video_id));
  };
  const disableBulk = disabled || !complete || isLoading;

  return (
    <fieldset className="workflow-video-selection" disabled={disabled} aria-label="Video scope">
      <div className="workflow-video-modes">
        <span className="workflow-video-index-name" title={indexName}>{indexName}</span>
        <div className="workflow-video-actions workflow-video-scope" role="radiogroup" aria-label="Video selection mode">
          <label className={`workflows-refresh ${!isSubset ? 'active' : ''}`}>
            <input type="radio" name={`video-scope-${groupId}`} checked={!isSubset} onChange={() => onChange(null)} />
            <span>All videos{allVideoCount !== undefined && ` (${allVideoCount.toLocaleString()})`}</span>
          </label>
          <label className={`workflows-refresh ${isSubset ? 'active' : ''}`}>
            <input type="radio" name={`video-scope-${groupId}`} checked={isSubset} onChange={() => onChange([])} />
            <span>Selected videos ({selectedIds.size.toLocaleString()})</span>
          </label>
        </div>
        {isSubset && (
          <button type="button" className="workflows-refresh workflow-video-toggle" aria-expanded={expanded}
            aria-controls={`video-grid-${groupId}`} onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Hide videos' : 'Show videos'} {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>
      {isSubset && (
        <div id={`video-grid-${groupId}`} hidden={!expanded}>
          <div className="workflow-video-toolbar">
            <div className="workflow-video-actions" role="group" aria-label="Select videos">
              <button type="button" className="workflows-refresh" disabled={disableBulk} onClick={() => onChange(videos.map((video) => video.video_id))}>Select all</button>
              <button type="button" className="workflows-refresh" disabled={disableBulk} onClick={() => selectByStatus(false)}>Unprocessed</button>
              <button type="button" className="workflows-refresh" disabled={disableBulk} onClick={() => selectByStatus(true)}>Processed</button>
              <button type="button" className="workflows-refresh" disabled={disabled || !selectedIds.size} onClick={() => onChange([])}>Clear selection</button>
            </div>
            <WorkflowSortControls fields={SORT_FIELDS} value={sort.field} ascending={sort.ascending}
              onChange={(field, ascending) => setSort({ field, ascending })} ariaLabel="Sort videos">
              <button type="button" className="workflows-refresh" disabled={isLoading} aria-label="Refresh videos"
                onClick={() => setRefreshRevision((value) => value + 1)}><RefreshCw size={14} /></button>
            </WorkflowSortControls>
          </div>
          {currentDataset?.error && (
            <div className="workflows-inline-error" role="alert">
              {currentDataset.error}
              <button type="button" onClick={() => setRefreshRevision((value) => value + 1)}>Retry</button>
            </div>
          )}
          {isLoading && <p className="workflow-step-help" role="status">Loading videos…</p>}
          {complete && !videos.length && <p className="workflow-step-help">No videos in this index.</p>}
          <PreviewViewportProvider>
            <div className="workflow-video-grid" aria-label="Videos" aria-busy={isLoading}>
              {sortedVideos.map((video) => {
                const run = selectVideoRun(video.video_id, runCatalog);
                return (
                <DeferredPreviewTile key={video.video_id}>
                  <VideoTile video={video} indexId={indexId} onTileClick={toggleVideo} onDelete={ignoreDelete}
                    runScopeId={run?.run_id} runStatus={run?.status} runLiveness={run?.liveness}
                    isProcessing={getEffectiveVideoStatus(video) === 'processing'} isReadOnly isInteractive={!disabled}
                    isSelected={selectedIds.has(video.video_id)} selectionMode infoMode="compact" showMarker={false} showMetadataKeys={false} />
                </DeferredPreviewTile>
              ); })}
            </div>
          </PreviewViewportProvider>
        </div>
      )}
    </fieldset>
  );
};

export default WorkflowVideoSelection;
